import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { CourseScene } from '@/three/scenes/CourseScene';
import { ShotData } from '@/types/shot.types';
import { PlayerLabel } from './PlayerLabel';
import { EventType, QueueState } from '@/types/director.types';
import { CameraPreset } from '@/types/camera.types';
import { EventQueueManager } from '@/services/EventQueueManager';
import { EventConfig } from '@/types/event.types';
import { CourseConfig } from '@/types/course.types';

interface CourseViewerProps {
  eventConfig: EventConfig;
  queueManager: EventQueueManager | null;
  queueState: QueueState;
  onSceneReady?: (scene: CourseScene | null) => void;
}

/**
 * CourseViewer component
 * Main component that creates and manages the three.js scene
 * Handles canvas lifecycle, animation loop, and event-driven shot/camera updates
 */
export function CourseViewer({ eventConfig, queueManager, queueState, onSceneReady }: CourseViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<CourseScene | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [labelData, setLabelData] = useState<
    Array<{
      id: string;
      playerInfo: ShotData['playerInfo'];
      color: number;
      position: [number, number, number];
    }>
  >([]);

  // Track current shot being animated for completion detection
  const currentShotId = useRef<string | null>(null);

  // Initialize scene
  useEffect(() => {
    if (!canvasRef.current) return;

    // Convert EventConfig.course to CourseConfig format
    const courseConfig: CourseConfig = {
      bounds: eventConfig.course.bounds,
      width: eventConfig.course.width,
      height: eventConfig.course.height,
      textureUrl: eventConfig.course.terrainAssetUrl,
    };

    // Create scene with config from API
    const scene = new CourseScene(canvasRef.current, courseConfig);
    sceneRef.current = scene;

    // Notify parent that scene is ready
    onSceneReady?.(scene);

    // Set initial camera position (default view from event config)
    const initialPreset = eventConfig.cameraPresets.default;
    if (initialPreset) {
      scene.setCameraPreset(initialPreset, false);
    }

    console.log('[CourseViewer] Scene initialized with event config', {
      eventName: eventConfig.event.name,
      courseName: eventConfig.course.name,
    });

    // Expose developer helper functions to window for console access
    // These allow capturing camera positions for creating presets
    (window as any).getCameraSettings = () => {
      if (!sceneRef.current) {
        console.error('Scene not initialized');
        return null;
      }

      const settings = sceneRef.current.getCameraSettings();

      console.log('Current Camera Settings:');
      console.log(JSON.stringify(settings, null, 2));
      console.log('\nCopy this for your cameraPresets.ts file:');
      console.log(`{
  position: {
    x: ${settings.position.x},
    y: ${settings.position.y},
    z: ${settings.position.z},
  },
  target: {
    x: ${settings.target.x},
    y: ${settings.target.y},
    z: ${settings.target.z},
  },
}`);

      return settings;
    };

    (window as any).moveCameraTo = (
      position: { x: number; y: number; z: number },
      target: { x: number; y: number; z: number },
      duration: number = 3
    ) => {
      if (!sceneRef.current) {
        console.error('Scene not initialized');
        return;
      }

      sceneRef.current.moveCameraTo(position, target, duration);
      console.log('Camera moving to new position...');
    };

    console.log('Shot Tracer initialized');
    console.log('Developer tools available:');
    console.log('- getCameraSettings(): Get current camera position and target');
    console.log('- moveCameraTo(position, target, duration): Animate camera to a saved position');
    console.log('\nExample usage:');
    console.log('  getCameraSettings()');
    console.log('  moveCameraTo({ x: 100, y: 200, z: 300 }, { x: 0, y: 0, z: 0 }, 3)');

    // Animation loop
    const animate = () => {
      if (sceneRef.current) {
        sceneRef.current.render();

        // Update label positions
        const activeShots = sceneRef.current.getActiveShots();
        const newLabelData = Array.from(activeShots.entries()).map(
          ([id, tracer]) => {
            const pos = tracer.getLabelPosition();
            return {
              id,
              playerInfo: tracer.getPlayerInfo(),
              color: tracer.getColor(),
              position: [pos.x, pos.y, pos.z] as [number, number, number],
            };
          }
        );
        setLabelData(newLabelData);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      if (sceneRef.current) {
        sceneRef.current.handleResize();
      }
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('resize', handleResize);

      // Clean up window functions
      delete (window as any).getCameraSettings;
      delete (window as any).moveCameraTo;

      // Notify parent that scene is being destroyed
      onSceneReady?.(null);

      if (sceneRef.current) {
        sceneRef.current.dispose();
      }
    };
  }, [eventConfig, onSceneReady]);

  // Process events from the queue
  useEffect(() => {
    if (!sceneRef.current || !queueManager) return;

    const currentEvent = queueState.currentEvent;
    if (!currentEvent) return;

    const scene = sceneRef.current;

    if (currentEvent.type === EventType.CAMERA_MOVE) {
      // Execute camera move
      const preset = currentEvent.payload as CameraPreset;
      console.log('[CourseViewer] Executing camera move event');
      scene.animateCameraTo(preset, 3);

      // Monitor camera animation completion
      const checkCameraComplete = setInterval(() => {
        if (!scene.getCamera) return;

        // Check if camera is still animating via the controller
        const cameraController = (scene as any).cameraController;
        if (cameraController && !cameraController.isAnimating()) {
          console.log('[CourseViewer] Camera move complete');
          clearInterval(checkCameraComplete);
          queueManager.markCurrentEventComplete();
        }
      }, 100);

      // Cleanup interval on unmount
      return () => clearInterval(checkCameraComplete);
    } else if (currentEvent.type === EventType.SHOT_TRACE) {
      // Execute shot trace
      const shot = currentEvent.payload as ShotData;
      console.log(`[CourseViewer] Executing shot trace event: ${shot.id}`);
      scene.addShot(shot);
      currentShotId.current = shot.id;

      // Monitor shot animation completion
      const checkShotComplete = setInterval(() => {
        const activeShots = scene.getActiveShots();
        const shotTracer = activeShots.get(shot.id);

        if (shotTracer && shotTracer.isComplete()) {
          console.log(`[CourseViewer] Shot trace complete: ${shot.id}`);
          clearInterval(checkShotComplete);
          currentShotId.current = null;
          queueManager.markCurrentEventComplete();
        }
      }, 100);

      // Cleanup interval on unmount
      return () => clearInterval(checkShotComplete);
    }
  }, [queueState.currentEvent, queueManager]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <canvas ref={canvasRef} />

      {/* Player labels overlay */}
      {sceneRef.current &&
        labelData.map((label) => (
          <PlayerLabel
            key={label.id}
            playerInfo={label.playerInfo}
            color={label.color}
            position={
              new THREE.Vector3(
                label.position[0],
                label.position[1],
                label.position[2]
              )
            }
            camera={sceneRef.current!.getCamera()}
          />
        ))}
    </div>
  );
}
